import type { SpeckleStreamMode } from "@/api/services/ProjectRevision";

// Re-exported so the component and both dialogs name the mode through this module — the one place the
// choice's rules live — instead of reaching into the API service for a type the UI reasons about.
export type { SpeckleStreamMode };

// The clone / restore Speckle choice, as plain data — what SpeckleStreamChoice.vue renders and what
// the two modals send. It lives outside the component so both modals ask the same question the same
// way, and so the rules are readable (and testable) without mounting anything.
//
// `speckleStreamId` names a Speckle PROJECT: Speckle renamed streams to projects (and branches to
// models) and deprecated `Stream` in its GraphQL schema, but our Hasura column kept the old word.
// The identifiers here follow the column; the user-facing strings follow Speckle.

/**
 * What the user picked. `speckleStreamId` is a Speckle project id, and the only one this choice can
 * produce is the SOURCE project's own: the question is "a Speckle project of its own, or the one the
 * project being copied already uses", not a browse of everything the tenant can see.
 */
export type SpeckleStreamSelection = {
  mode: SpeckleStreamMode;
  speckleStreamId?: string;
};

/**
 * A brand-new Speckle project, matching the API's own default: a copy that silently shares the
 * source's models is the bug this choice exists to fix, so nothing but an explicit choice keeps one.
 * Also what the modals hold before the block mounts, and what they keep when it never does — a
 * source with no Speckle project has no id for `ExistingStream` to resolve to.
 */
export const NEW_STREAM_SELECTION: SpeckleStreamSelection = { mode: "NewStream" };

/**
 * The option labels, in the block's Speckle-side vocabulary: inside "Speckle Integration", "project"
 * means a Speckle project. One map so the two orderings below cannot drift apart in wording.
 */
export const SPECKLE_STREAM_MODE_LABELS: Record<SpeckleStreamMode, string> = {
  NewStream: "Create new project",
  ExistingStream: "Use the same project",
};

/**
 * Which answer a dialog leads with — its default AND the first radio, because the two must agree:
 * a pre-selected second option reads as a correction of the first.
 *
 * Cloning and restoring lead with opposite answers because they are opposite intentions. A clone is
 * a new line of work — a variant, a what-if — so it wants its own Speckle project. A restore brings
 * back a state that WAS this project, so it wants to stay wired to the same models the project is
 * already fed by. Neither is forced: both options remain, in both dialogs.
 */
export const CLONE_PREFERRED_MODE: SpeckleStreamMode = "NewStream";
export const RESTORE_PREFERRED_MODE: SpeckleStreamMode = "ExistingStream";

/**
 * The two options with the preferred one first — the whole ordering rule, in one place, derived from
 * the same value that seeds the selection so a caller cannot pick a default the list contradicts.
 */
export const speckleStreamModeOptions = (preferredMode: SpeckleStreamMode): { value: SpeckleStreamMode; label: string }[] => {
  const other: SpeckleStreamMode = preferredMode === "NewStream" ? "ExistingStream" : "NewStream";
  return [preferredMode, other].map((mode) => ({ value: mode, label: SPECKLE_STREAM_MODE_LABELS[mode] }));
};

/**
 * Whether the choice is worth offering at all: a source building project with no Speckle project has
 * nothing to keep and nothing to replace, so the API ignores both fields and the block would ask a
 * question with no consequence (RTLCA-5339).
 */
export const offersSpeckleStreamChoice = (sourceSpeckleStreamId: string | null | undefined): boolean =>
  typeof sourceSpeckleStreamId === "string" && sourceSpeckleStreamId.trim().length > 0;

/**
 * The selection a mode stands for. `ExistingStream` always resolves to the SOURCE project's own
 * Speckle project — which is what makes this option unrejectable: the API short-circuits an id equal
 * to the source's, asking Speckle nothing, so it needs no linked Speckle account and cannot fail for
 * having no models in common. Nothing here can produce `ExistingStream` without an id, which is why
 * there is no blocker to guard the confirm button with.
 */
export const selectionForMode = (mode: SpeckleStreamMode, sourceSpeckleStreamId: string): SpeckleStreamSelection =>
  mode === "ExistingStream" ? { mode, speckleStreamId: sourceSpeckleStreamId } : { mode: "NewStream" };
